import { toItem, type VaultItem } from '../../../library/vault/vaultData'
import { OF, type QueryValue } from '../../ofApi'

/**
 * onlyfans.com/my/discover: the OFTV channel's posts as a video grid
 * (bundle chunk `discover`: DiscoverPosts / DiscoverItem / PostFilter,
 * captured 2026-10-22). Pure helpers for the page and its requests.
 */

type Raw = Record<string, unknown>

const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : typeof v === 'string' && /^\d+$/.test(v) ? Number(v) : 0)
const str = (v: unknown) => (typeof v === 'string' ? v : '')

export const REFERER = `${OF}/my/discover`
/** The channel Discover shows (`users/oftv`). */
export const CHANNEL = 'oftv'
/** OnlyFans asks 21 posts a page (3 columns × 7 rows). */
export const PAGE = 21
export const MAX_AVATARS = 3

export type SortField = 'publish_date' | 'favorites_count' | 'tips_summ'
export type SortDir = 'asc' | 'desc'
export const SORT_FIELDS: SortField[] = ['publish_date', 'favorites_count', 'tips_summ']
export const SORT_DIRS: SortDir[] = ['asc', 'desc']

// ── Posts ────────────────────────────────────────────────────────────────

export interface DiscoverMedia {
  id: number
  type: string
  /** The tile picture (`files.preview.url`). */
  preview: string
  /** The full file (`files.full.url`); a photo without one cannot open. */
  src: string
  /** Seconds; 0 for photos. */
  duration: number
  isVideo: boolean
  /** What the viewer plays / shows. */
  item: VaultItem
}

export interface DiscoverPost {
  id: number
  authorId: number
  postedAt: string
  postedAtPrecise: string
  favoritesCount: number
  mediaCount: number
  isAddedToBookmarks: boolean
  /** mentionedUsers ×2, linkedUsers, promotionContent — deduplicated, in that order (DiscoverItem.allUserIds). */
  userIds: number[]
  media: DiscoverMedia | null
}

const fileUrl = (raw: Raw, key: string) => str(((raw.files as Raw | undefined)?.[key] as Raw | undefined)?.url)

/** OnlyFans' isVideo: not audio, and a video (or a gif made video, or an .mp4 source). */
export function isVideoMedia(raw: Raw): boolean {
  const type = str(raw.type)
  if (type === 'audio') return false
  if (type === 'video' || str(raw.mediaType) === 'video') return true
  if (raw.convertedToVideo === true && type === 'gif') return true
  const src = fileUrl(raw, 'full') || str(raw.sourceUrl)
  return (src.split('?')[0]?.split('.').pop() ?? '').toLowerCase() === 'mp4'
}

const idOf = (u: unknown) => num(typeof u === 'object' && u ? (u as Raw).id : u)

export function toDiscoverPost(raw: Raw): DiscoverPost {
  const list = (k: string) => (Array.isArray(raw[k]) ? (raw[k] as unknown[]) : [])
  const mentioned = list('mentionedUsers').map(idOf)
  const linked = list('linkedUsers').map(idOf)
  const promoted = list('promotionContent').map((p) => num((p as Raw | null)?.userId))
  const userIds = [...new Set([...mentioned, ...mentioned, ...linked, ...promoted].filter((n) => n > 0))]
  const first = list('media')[0] as Raw | undefined
  let media: DiscoverMedia | null = null
  if (first) {
    const isVideo = isVideoMedia(first)
    const item = toItem(first)
    media = {
      id: num(first.id),
      type: str(first.type),
      preview: fileUrl(first, 'preview'),
      src: fileUrl(first, 'full') || str(first.sourceUrl),
      duration: num(first.duration),
      isVideo,
      // The viewer dates the item by the post (OnlyFans passes createdAt: postedAt).
      item: { ...item, type: isVideo ? 'video' : item.type, createdAt: str(raw.postedAt) || item.createdAt },
    }
  }
  return {
    id: num(raw.id),
    authorId: idOf(raw.author),
    postedAt: str(raw.postedAt),
    postedAtPrecise: str(raw.postedAtPrecise),
    favoritesCount: num(raw.favoritesCount),
    mediaCount: num(raw.mediaCount) || list('media').length,
    isAddedToBookmarks: raw.isAddedToBookmarks === true,
    userIds,
    media,
  }
}

/** The grid shows only posts whose first media has a preview picture (DiscoverPosts.posts). */
export const isShown = (p: DiscoverPost) => Boolean(p.media?.preview)

/** Unix seconds with six decimals, the way OnlyFans pages posts. */
function precise(p: DiscoverPost): string {
  if (p.postedAtPrecise) return p.postedAtPrecise
  const ms = Date.parse(p.postedAt)
  return Number.isFinite(ms) ? `${Math.floor(ms / 1000)}.000000` : ''
}

// ── The posts request ────────────────────────────────────────────────────

/** The route query OnlyFans keeps the view in: ?q=…&order=favorites_count_desc. */
export interface DiscoverView {
  q: string
  field: SortField
  dir: SortDir
}

export const DEFAULT_VIEW: DiscoverView = { q: '', field: 'publish_date', dir: 'desc' }

export function parseView(query: string): DiscoverView {
  const p = new URLSearchParams(query)
  const order = p.get('order') ?? ''
  const m = /^(.+)_(asc|desc)$/.exec(order)
  const field = m && SORT_FIELDS.includes(m[1] as SortField) ? (m[1] as SortField) : 'publish_date'
  const dir = m ? (m[2] as SortDir) : 'desc'
  return { q: p.get('q') ?? '', field, dir }
}

/** PostFilter.modelsChangeHandler: `order` only when not the default, `q` only when set. */
export function viewQuery(v: DiscoverView): string {
  const p = new URLSearchParams()
  if (v.q) p.set('q', v.q)
  const order = `${v.field}_${v.dir}`
  if (order !== 'publish_date_desc') p.set('order', order)
  return p.toString()
}

export const orderOf = (v: DiscoverView) => `${v.field}_${v.dir}`

/**
 * `users/<id>/posts` query, keys in OnlyFans' order:
 * limit, order, skip_users, format, query, counters, before/afterPublishTime.
 * Ascending orders page forward (afterPublishTime), the rest backward.
 */
export function postsQuery(v: DiscoverView, marker: string | null, first: boolean): Record<string, QueryValue> {
  const asc = v.dir === 'asc'
  return {
    limit: PAGE,
    order: orderOf(v),
    skip_users: 'all',
    format: 'infinite',
    query: v.q || undefined,
    counters: first ? 1 : 0,
    ...(marker ? (asc ? { afterPublishTime: marker } : { beforePublishTime: marker }) : {}),
  }
}

/**
 * The next page's marker: the last shown post's time, folded with the page's
 * head/tail markers (posts store setMarkers) — the oldest for a descending
 * list, the newest for an ascending one.
 */
export function nextMarker(dir: SortDir, prev: string | null, page: { headMarker?: unknown; tailMarker?: unknown }, posts: DiscoverPost[]): string | null {
  const shown = posts.filter(isShown)
  const last = shown[shown.length - 1]
  const values = [prev, str(page.headMarker), str(page.tailMarker), last ? precise(last) : '']
    .map((s) => (s ? Number(s) : NaN))
    .filter((n) => Number.isFinite(n) && n > 0)
  if (!values.length) return null
  return (dir === 'asc' ? Math.max(...values) : Math.min(...values)).toFixed(6)
}

/** "Go to date": the end (descending) or start (ascending) of that local day, as OnlyFans' marker. */
export function goToDateMarker(date: string, dir: SortDir): string {
  const [y = 0, m = 1, d = 1] = date.split('-').map(Number)
  if (dir === 'asc') return `${Math.floor(new Date(y, m - 1, d, 0, 0, 0).getTime() / 1000)}.000000`
  return `${Math.floor(new Date(y, m - 1, d, 23, 59, 59).getTime() / 1000)}.999999`
}

// ── Users ────────────────────────────────────────────────────────────────

export interface DiscoverUser {
  id: number
  name: string
  username: string
  avatar: string
  cover: string
  verified: boolean
  isFree: boolean
  /** OnlyFans' isActiveUser: has a username, not deleted, not deactivated. */
  active: boolean
}

function thumb(raw: Raw, field: string, thumbs: string, sizes: string[]): string {
  const set = (raw[thumbs] ?? {}) as Raw
  for (const s of sizes) if (str(set[s])) return str(set[s])
  return str(raw[field])
}

/** Names come HTML-escaped ("Jade &amp; Joey"). */
export function decodeEntities(s: string): string {
  return s.replace(/&(#x?[0-9a-f]+|amp|lt|gt|quot|apos|#39);/gi, (all, code: string) => {
    const c = code.toLowerCase()
    if (c === 'amp') return '&'
    if (c === 'lt') return '<'
    if (c === 'gt') return '>'
    if (c === 'quot') return '"'
    if (c === 'apos' || c === '#39') return "'"
    const n = c.startsWith('#x') ? parseInt(c.slice(2), 16) : parseInt(c.slice(1), 10)
    return Number.isFinite(n) ? String.fromCodePoint(n) : all
  })
}

export function toDiscoverUser(raw: Raw): DiscoverUser {
  const username = str(raw.username)
  return {
    id: num(raw.id),
    name: decodeEntities(str(raw.name)),
    username,
    avatar: thumb(raw, 'avatar', 'avatarThumbs', ['c144', 'c50']),
    cover: thumb(raw, 'header', 'headerThumbs', ['w480', 'w760']),
    verified: raw.isVerified === true,
    isFree: raw.isFree === true || num(raw.subscribePrice) === 0,
    active: Boolean(username) && raw.isDeleted !== true && raw.isActive !== false,
  }
}

/** `users/list` answers `{ "<id>": user }`. */
export function toUserMap(payload: unknown): Record<number, DiscoverUser> {
  const out: Record<number, DiscoverUser> = {}
  if (!payload || typeof payload !== 'object') return out
  for (const raw of Object.values(payload as Record<string, Raw>)) {
    if (!raw || typeof raw !== 'object') continue
    const u = toDiscoverUser(raw)
    if (u.id) out[u.id] = u
  }
  return out
}

/**
 * The creators a page of posts carries as `_view: "r"` (its linked users):
 * OnlyFans resolves them before showing the page, ascending, ten per request.
 */
export function pageUserIds(rawList: Raw[]): number[] {
  const ids = new Set<number>()
  for (const raw of rawList) {
    for (const key of ['mentionedUsers', 'linkedUsers']) {
      const list = Array.isArray(raw[key]) ? (raw[key] as Raw[]) : []
      for (const u of list) if (u && u._view === 'r' && num(u.id) > 0) ids.add(num(u.id))
    }
  }
  return [...ids]
}

/** Ascending, `size` per request (users/getUsersIfNeeded). */
export function batches(ids: number[], size = 10): number[][] {
  const sorted = [...new Set(ids)].filter((n) => n > 0).sort((a, b) => a - b)
  const out: number[][] = []
  for (let i = 0; i < sorted.length; i += size) out.push(sorted.slice(i, i + size))
  return out
}

/** Who a tile names: its active creators, else the post's author (DiscoverItem.linkedUsers). */
export function tileUserIds(post: DiscoverPost, users: Record<number, DiscoverUser>): number[] {
  const visible = post.userIds.filter((id) => users[id]?.active)
  return visible.length ? visible : [post.authorId]
}

export const profileUrl = (u: Pick<DiscoverUser, 'username' | 'id'> | undefined, id: number) => `${OF}/${u?.username || id}`

// ── Labels ───────────────────────────────────────────────────────────────

/** dateFromNow: "45sec", "12min", "3h", "6d", "2w", "5m", "1y" (OnlyFans' own short units). */
export function timeAgo(iso: string, now: Date = new Date()): string {
  const then = new Date(iso)
  if (!Number.isFinite(then.getTime())) return ''
  const sec = Math.floor((now.getTime() - then.getTime()) / 1000)
  if (sec < 60) return `${Math.max(0, sec)}sec`
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min}min`
  const h = Math.floor(min / 60)
  if (h < 24) return `${h}h`
  const d = Math.floor(h / 24)
  if (d < 7) return `${d}d`
  const w = Math.floor(d / 7)
  if (w < 4) return `${w}w`
  let months = (now.getFullYear() - then.getFullYear()) * 12 + (now.getMonth() - then.getMonth())
  const anniversary = new Date(then)
  anniversary.setMonth(then.getMonth() + months)
  if (anniversary.getTime() > now.getTime()) months -= 1
  if (months < 12) return months <= 0 ? '1m' : `${months}m`
  return `${Math.floor(months / 12)}y`
}

/** "4:08" under an hour, "1:02:03" from an hour (getDurationFormat). */
export function durationLabel(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const pad = (n: number) => String(n).padStart(2, '0')
  return h > 0 ? `${h}:${pad(m)}:${pad(s % 60)}` : `${m}:${pad(s % 60)}`
}

// ── The viewer ───────────────────────────────────────────────────────────

/** What the viewer pages through (videos and photos with a source), and where each tile opens it. */
export function viewerItems(posts: DiscoverPost[]): { items: VaultItem[]; indexOf: Map<number, number> } {
  const items: VaultItem[] = []
  const indexOf = new Map<number, number>()
  for (const p of posts) {
    if (!p.media) continue
    if (!p.media.isVideo && !p.media.src) continue
    indexOf.set(p.id, items.length)
    items.push(p.media.item)
  }
  return { items, indexOf }
}

// ── Search ───────────────────────────────────────────────────────────────

/** The search field: no leading spaces, at most 100 characters. */
export const cleanSearch = (s: string) => s.replace(/^\s+/, '').slice(0, 100)

/** Recent searches, newest first, without repeats. */
export function addRecent(list: string[], q: string, max = 10): string[] {
  const t = q.trim()
  if (!t) return list
  return [t, ...list.filter((x) => x.toLowerCase() !== t.toLowerCase())].slice(0, max)
}

// ── Bookmark categories (Add to bookmarks) ──────────────────────────────

export interface BookmarkCategory {
  id: number
  name: string
  postCount: number
  /** This post is in it. */
  hasPost: boolean
}

export function toCategory(raw: Raw): BookmarkCategory {
  return { id: num(raw.id), name: decodeEntities(str(raw.name)), postCount: num(raw.postCount), hasPost: raw.hasPost === true }
}

/** `posts/bookmarks/categories?limit=10&offset=…&postId=…&view=add_post`, as OnlyFans builds it. */
export function categoriesQuery(postId: number, offset: number): Record<string, QueryValue> {
  return { limit: 10, offset, postId, view: 'add_post' }
}
